import { format } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WorkflowMonth } from '@/pages/WorkflowV2/api';

/** First-of-month ISO → local Date (date part only, no timezone shift) */
function monthDate(month: WorkflowMonth, offset = 0): Date {
  const [y, m] = month.month.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1 + offset, 1);
}

export function workflowMonthLabel(month: WorkflowMonth): string {
  return format(monthDate(month), 'MMMM yyyy');
}

/** The workflow month works the PREVIOUS month's books */
export function booksMonthLabel(month: WorkflowMonth): string {
  return format(monthDate(month, -1), 'MMMM yyyy');
}

interface MonthPickerProps {
  /** Newest first (listWorkflowMonths order) */
  months: WorkflowMonth[];
  selected: WorkflowMonth | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

/**
 * Workflow month dropdown with previous/next steppers. Lists only months
 * that actually have a workflow sheet, so every choice has data.
 */
export default function MonthPicker({
  months,
  selected,
  onSelect,
  disabled,
}: Readonly<MonthPickerProps>) {
  const index = selected ? months.findIndex(m => m.id === selected.id) : -1;
  // months are newest first: "previous" is the next index
  const older = index >= 0 ? months[index + 1] : undefined;
  const newer = index > 0 ? months[index - 1] : undefined;

  const stepClass =
    'inline-flex h-9 w-9 items-center justify-center rounded-md border border-modern-charcoal bg-modern-charcoal text-white shadow-sm transition-colors hover:bg-modern-light-charcoal disabled:cursor-not-allowed disabled:opacity-30';

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className={stepClass}
        onClick={() => older && onSelect(older.id)}
        disabled={disabled || !older}
        aria-label="Previous month"
        title={older ? workflowMonthLabel(older) : undefined}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <Select
        value={selected?.id}
        onValueChange={onSelect}
        disabled={disabled || months.length === 0}
      >
        <SelectTrigger
          className="h-9 w-[190px] justify-start gap-2 bg-white font-medium"
          aria-label="Workflow month"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-modern-charcoal" />
          {/* Explicit children: the trigger shows just the month, not the
              item's "… books" hint */}
          <span className="flex-1 text-left">
            <SelectValue placeholder="Select a month">
              {selected ? workflowMonthLabel(selected) : undefined}
            </SelectValue>
          </span>
        </SelectTrigger>
        <SelectContent>
          {months.map(m => (
            <SelectItem key={m.id} value={m.id}>
              <span className="font-medium">{workflowMonthLabel(m)}</span>
              <span className="ml-2 text-xs text-slate-500">
                {booksMonthLabel(m)} books
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        className={stepClass}
        onClick={() => newer && onSelect(newer.id)}
        disabled={disabled || !newer}
        aria-label="Next month"
        title={newer ? workflowMonthLabel(newer) : undefined}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
